// Google-Sheets "Connected Sheet" header. Tells the operator
// where the data lives + how to refresh + how to inspect the
// connection. The actual schema/refresh actions are owned by the
// parent; this component is just the labelled row.
//
// Mirrors the Google Sheets layout:
//   [icon] Connected Sheet · <view name>      [Refresh] [Refresh options ▾]
//                                              [Connection settings ⚙]
//   PREVIEW · N rows · last refreshed Ns ago

import * as React from 'react';

const BQ_CONSOLE_PREFIX = 'https://console.cloud.google.com/bigquery';

export function ConnectedSheetHeader({
  dataset,
  viewName,
  rowCount,
  lastRefreshAt,
  refreshIntervalMs,
  loading,
  onRefresh,
  onChangeRefreshInterval,
  rangeLabel,
}: {
  dataset: string;
  viewName: string;
  rowCount: number | null;
  lastRefreshAt: number | null;
  refreshIntervalMs: number;
  loading: boolean;
  onRefresh: () => void;
  onChangeRefreshInterval: (ms: number) => void;
  rangeLabel?: string;
}): React.ReactElement {
  const [showRefreshMenu, setShowRefreshMenu] = React.useState(false);
  const project = process.env.NEXT_PUBLIC_GCP_PROJECT ?? 'hub-mega-prod';
  const bqUrl = `${BQ_CONSOLE_PREFIX}?project=${project}&ws=!1m4!1m3!3m2!1s${project}!2s${dataset}`;
  const fmtAge = (ms: number | null): string => {
    if (ms === null) return 'never';
    const s = Math.max(0, Math.floor((Date.now() - ms) / 1000));
    if (s < 60) return `${s}s ago`;
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    return `${Math.floor(s / 3600)}h ago`;
  };
  const fmtInterval = (ms: number): string => {
    if (ms < 60_000) return `${Math.round(ms / 1000)}s`;
    if (ms < 3_600_000) return `${Math.round(ms / 60_000)}m`;
    return `${Math.round(ms / 3_600_000)}h`;
  };
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', gap: 6,
      padding: '8px 12px',
      background: 'var(--bg-elev)',
      border: '1px solid var(--line)',
      borderRadius: 6,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          fontFamily: 'var(--ui)', fontSize: 13, color: 'var(--ink-2)',
        }}>
          <span aria-hidden style={{ fontSize: 14 }}>⛁</span>
          <strong style={{ color: 'var(--ink)' }}>Connected Sheet</strong>
          <span style={{ color: 'var(--ink-3)' }}>·</span>
          <code style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--ink-2)' }}>{viewName}</code>
        </span>
        <span style={{ flex: 1 }} />
        <button
          type="button"
          onClick={onRefresh}
          disabled={loading}
          className="btn sm ghost"
          style={{ padding: '3px 10px', fontSize: 11 }}
          title="Re-query the view now"
        >{loading ? 'Refreshing…' : '↻ Refresh'}</button>
        <div style={{ position: 'relative' }}>
          <button
            type="button"
            onClick={() => setShowRefreshMenu(o => !o)}
            className="btn sm ghost"
            style={{ padding: '3px 10px', fontSize: 11 }}
            title={`Auto-refresh every ${fmtInterval(refreshIntervalMs)}`}
          >Refresh options ▾</button>
          {showRefreshMenu && (
            <div
              onMouseLeave={() => setShowRefreshMenu(false)}
              style={{
                position: 'absolute', top: 'calc(100% + 4px)', right: 0,
                background: 'var(--bg-elev)', border: '1px solid var(--line)',
                borderRadius: 6, padding: 6, minWidth: 220,
                boxShadow: '0 6px 20px oklch(0.18 0.01 250 / 0.18)',
                zIndex: 1100,
              }}
            >
              <div style={{ padding: '4px 8px', fontSize: 10, fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--ink-3)' }}>
                Auto-refresh interval
              </div>
              {[
                { ms: 5_000,   label: 'Every 5 seconds' },
                { ms: 30_000,  label: 'Every 30 seconds' },
                { ms: 60_000,  label: 'Every minute' },
                { ms: 300_000, label: 'Every 5 minutes' },
                { ms: 0,       label: 'Manual only' },
              ].map(opt => {
                const active = refreshIntervalMs === opt.ms;
                return (
                  <button
                    key={opt.ms}
                    type="button"
                    onClick={() => { onChangeRefreshInterval(opt.ms); setShowRefreshMenu(false); }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      width: '100%', padding: '5px 8px',
                      background: active ? 'var(--accent-soft)' : 'transparent',
                      border: 'none', borderRadius: 4,
                      color: active ? 'var(--accent)' : 'var(--ink-2)',
                      fontFamily: 'var(--ui)', fontSize: 12,
                      cursor: 'pointer', textAlign: 'left',
                    }}
                    onMouseEnter={e => { if (!active) e.currentTarget.style.background = 'var(--bg-sunken)'; }}
                    onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}
                  >
                    <span style={{ width: 12, color: active ? 'var(--accent)' : 'transparent' }}>✓</span>
                    <span>{opt.label}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
        <a
          href={bqUrl}
          target="_blank"
          rel="noreferrer"
          className="btn sm ghost"
          style={{ padding: '3px 10px', fontSize: 11, textDecoration: 'none' }}
          title="Open the source's BigQuery dataset in the GCP Console"
        >⚙ Connection settings</a>
      </div>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        fontSize: 10.5, fontFamily: 'var(--mono)',
        textTransform: 'uppercase', letterSpacing: '0.08em',
        color: 'var(--ink-3)',
      }}>
        <span style={{ color: 'var(--accent)', fontWeight: 600 }}>Preview</span>
        {rowCount !== null && <span>· {rowCount.toLocaleString()} row{rowCount === 1 ? '' : 's'}</span>}
        {rangeLabel && <span>· {rangeLabel}</span>}
        <span>· last refresh {fmtAge(lastRefreshAt)}</span>
        <span>· auto every {fmtInterval(refreshIntervalMs) || 'manual'}</span>
      </div>
    </div>
  );
}
